import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Upload, Input, Select, Spin } from 'antd';
import ImgCrop from 'antd-img-crop';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useRegisterMutation, useGetProfileSignedUrlMutation } from '../services/authApi';
import { useUploadFileMutation } from 'services/api';
import AuthLayout from 'components/layout/auth-layout/auth-layout.component';
import { AuthButtonComponent } from '../components/auth-button/auth-button.component';
import { useAppSelector } from 'hooks/store';
import { selectGooglePlacesLoaded, setGooglePlacesLoaded } from 'redux/appSlice';
import { FormItemPlacesAutoComplete } from './form-item-places-auto-complete';
import { useDispatch } from 'react-redux';
import { beforeImage5mbUpload } from 'utils';
import { notificationApiError } from 'utils/ui';
import '../dentist-signup/signup.styles.less';
import { PasswordField } from '../components/password-field/password-field';

const { Option } = Select;

const ManufacturerSignupPage = () => {
  const dispatch = useDispatch();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState('');
  const [isProfilePicUploading, setIsProfilePicUploading] = useState(false);
  const [formData]: any = useState({ type: 'manufacturer' });
  const [register] = useRegisterMutation();
  const [getProfileSignedUrl] = useGetProfileSignedUrlMutation();
  const [uploadFile] = useUploadFileMutation();
  const googlePlacesLoaded = useAppSelector(selectGooglePlacesLoaded);

  // TODO: 1270
  setTimeout(() => dispatch(setGooglePlacesLoaded({ loading: true })), 500);

  const [form] = Form.useForm();
  const { push } = useHistory();

  const onFinishFailed = () => {
    // console.log('Failed:', errorInfo);
  };

  const onFinish = async (values: any) => {
    setFormSubmitting(true);
    const { type } = formData;
    const { confirmPassword, business, ...personalDetails } = values;

    const data = {
      type,
      personalDetails,
      profilePic,
      business,
    };

    try {
      const response = await register(data).unwrap();
      if (response) {
        push('/signup-success');
      }
    } catch (error: any) {
      notificationApiError(error);
    }
    setFormSubmitting(false);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }

    return e && e.fileList;
  };

  const profileOnChange = (info: any) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      setLoading(false);
    }
  };

  const profileRequest = async ({ file, onSuccess }: any) => {
    onSuccess('ok');
    if (file) {
      try {
        if (profilePic) {
          setProfilePic('');
        }
        setIsProfilePicUploading(true);
        const fileData = {
          fileName: file.name,
          mimeType: file.type,
          folderName: 'profile',
        };
        const signedUrl = await getProfileSignedUrl(fileData).unwrap();
        await uploadFile({ url: signedUrl.url, file });
        const imageUrl = signedUrl && signedUrl.url.split('?')[0];
        if (imageUrl) {
          setIsProfilePicUploading(false);
        }
        setProfilePic(imageUrl);
      } catch (e) {
        setIsProfilePicUploading(false);
      }
    }
  };

  const onPreview = async (file: any) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <AuthLayout background2 showGotoSignin>
      <div className="signup-page">
        <div className="grid mt-0">
          <div className="col-12">
            <div className="signup-right-container">
              <div className="signup-form-container">
                <h2 className="signup-text">Manufacturer Sign Up</h2>
                {/* <HalfBorder width={45} /> */}

                <Form
                  name="manufacturer-signup"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  size="large"
                  autoComplete="off"
                  layout="vertical"
                >
                  <div className="steps-content">
                    <div className="grid">
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item
                          name="firstName"
                          label="Contact First Name *"
                          rules={[
                            {
                              required: true,
                              message: 'Please input first name',
                            },
                          ]}
                        >
                          <Input className="custom-input" placeholder="John" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item
                          name="lastName"
                          label="Contact Last Name *"
                          // hasFeedback
                          rules={[
                            {
                              required: true,
                              message: 'Please input last name',
                            },
                          ]}
                        >
                          <Input className="custom-input" placeholder="Doe" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <PasswordField form={form} />
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item
                          name="confirmPassword"
                          label="Confirm Password *"
                          dependencies={['password']}
                          // hasFeedback
                          rules={[
                            {
                              required: true,
                              message: 'Please input confirm password',
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('The two passwords that you entered do not match!'));
                              },
                            }),
                          ]}
                        >
                          <Input.Password className="custom-input" placeholder="Enter Confirm Password" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item
                          name={['email', 'email']}
                          label="Email Address *"
                          rules={[
                            {
                              required: true,
                              message: 'Please enter your email address',
                            },
                            { type: 'email', message: 'Please enter your full email address' },
                          ]}
                          normalize={(value) => value?.toLowerCase()}
                        >
                          <Input className="custom-input" placeholder="Enter your email address" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item name={['business', 'name']} label="Business Name">
                          <Input className="custom-input" placeholder="Enter Business Name" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item
                          label="Mobile Number *"
                          // noStyle
                        >
                          <Input.Group compact className="flex">
                            <Form.Item
                              name={['mobile', 'ext']}
                              noStyle
                              rules={[{ required: true, message: 'ext is required' }]}
                            >
                              <Select className="custom-input" style={{ width: '97px' }} placeholder="ext">
                                <Option value="+44">
                                  <img
                                    src="https://cdn-icons-png.flaticon.com/512/555/555417.png"
                                    alt="UK falg"
                                    style={{ width: 20 }}
                                    className="mr-1"
                                  />{' '}
                                  +44
                                </Option>
                                <Option value="+1">
                                  <img
                                    src="https://cdn-icons-png.flaticon.com/512/206/206626.png"
                                    alt="US falg"
                                    style={{ width: 20 }}
                                    className="mr-1"
                                  />{' '}
                                  +1
                                </Option>
                              </Select>
                            </Form.Item>
                            <Form.Item
                              name={['mobile', 'number']}
                              noStyle
                              rules={[{ required: true, message: 'Please enter your mobile number' }]}
                            >
                              <Input className="custom-input flex-auto" placeholder="Enter your mobile number" />
                            </Form.Item>
                          </Input.Group>
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        {googlePlacesLoaded && (
                          <FormItemPlacesAutoComplete
                            label="Business Address *"
                            name={['business', 'address', 'formattedAddress']}
                            rules={[{ required: true, message: 'Please enter your business address' }]}
                            onSetFieldsValue={(val: any) => form.setFieldsValue(val)}
                          />
                        )}
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item name={['business', 'address', 'postcode']} label="Business Postcode">
                          <Input className="custom-input" placeholder="Enter your postcode" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item name={['business', 'address', 'country']} label="Business Country">
                          <Input className="custom-input" placeholder="Enter country" />
                        </Form.Item>
                      </div>
                      <div className="col-12 sm:col-12 md:col-6">
                        <Form.Item label="Would you like to upload a profile picture?" className="auth-upload-avatar">
                          <Form.Item name="profilePic" valuePropName="fileList" getValueFromEvent={normFile} noStyle>
                            <ImgCrop rotate>
                              <Upload
                                listType="picture-card"
                                showUploadList={false}
                                beforeUpload={beforeImage5mbUpload}
                                customRequest={profileRequest}
                                onChange={profileOnChange}
                                onPreview={onPreview}
                              >
                                {profilePic ? (
                                  <img src={profilePic} alt="avatar" style={{ width: '100%', height: '100px' }} />
                                ) : isProfilePicUploading ? (
                                  <Spin size="small" />
                                ) : (
                                  uploadButton
                                )}
                              </Upload>
                            </ImgCrop>
                          </Form.Item>
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                  <div className="steps-action">
                    <Form.Item>
                      <AuthButtonComponent isLoading={formSubmitting}>Submit</AuthButtonComponent>
                    </Form.Item>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ManufacturerSignupPage;
